// src/components/sales/pages/marketing-campaigns/MarketingCampaignsView.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  listMarketingCampaigns,
  createMarketingCampaign,
  updateMarketingCampaign,
  deleteMarketingCampaign,
  sendMarketingCampaign,
} from "../../../../services/marketingCampaigns.service";
import MarketingCampaignModal from "./MarketingCampaignModal";
import ModalPortal from "../../../shared/ui/ModalPortal";
import { Plus, RefreshCcw, Trash2, Pencil, Send } from "lucide-react";

const getId = (x) => x?.campaignId ?? x?.marketingCampaignId ?? x?.id;

function ConfirmModal({ open, title, message, confirmText = "Confirm", onCancel, onConfirm, busy }) {
  if (!open) return null;
  return (
    <ModalPortal onClose={busy ? undefined : onCancel}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onMouseDown={busy ? undefined : onCancel} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div
          className="w-full max-w-md bg-white/10 backdrop-blur-2xl border border-white/20 rounded-2xl overflow-hidden shadow-[0_0_40px_rgba(255,0,255,0.25)]"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="p-5 bg-gradient-to-r from-pink-600 to-purple-700 text-white">
            <div className="text-lg font-bold">{title}</div>
            <div className="text-xs text-white/80 mt-1">{message}</div>
          </div>
          <div className="p-5 flex gap-3 justify-end">
            <button
              disabled={busy}
              onClick={onCancel}
              className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              disabled={busy}
              onClick={onConfirm}
              className="px-4 py-2 rounded-xl bg-red-500/80 hover:bg-red-500 text-white disabled:opacity-50"
            >
              {busy ? "Working..." : confirmText}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

export default function MarketingCampaignsView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [confirm, setConfirm] = useState({
    open: false,
    title: "",
    message: "",
    busy: false,
    onConfirm: null,
  });

  const closeConfirm = () => setConfirm((p) => ({ ...p, open: false, busy: false, onConfirm: null }));

  const load = useCallback(async () => {
    setErr("");
    setLoading(true);
    try {
      const res = await listMarketingCampaigns();
      // support API shapes: {data:[...]} or [...]
      const list = Array.isArray(res) ? res : res?.data ?? [];
      setRows(list);
    } catch (e) {
      setErr(e?.message || "Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setModalOpen(true);
  };

  const onSubmitModal = async (dto) => {
    setErr("");
    try {
      if (editing) {
        const id = getId(editing);
        await updateMarketingCampaign(id, dto);
      } else {
        await createMarketingCampaign(dto);
      }
      setModalOpen(false);
      setEditing(null);
      await load();
    } catch (e) {
      // show server validation errors clearly
      setErr(e?.message || "Save failed");
      // keep modal open so user can fix
      throw e;
    }
  };

  const askDelete = (item) => {
    const id = getId(item);
    setConfirm({
      open: true,
      title: "Delete Campaign",
      message: `Are you sure you want to delete "${item?.campaignName || id}"?`,
      busy: false,
      onConfirm: async () => {
        setConfirm((p) => ({ ...p, busy: true }));
        try {
          await deleteMarketingCampaign(id);
          closeConfirm();
          await load();
        } catch (e) {
          setErr(e?.message || "Delete failed");
          setConfirm((p) => ({ ...p, busy: false }));
        }
      },
    });
  };

  const askSend = (item) => {
    const id = getId(item);
    setConfirm({
      open: true,
      title: "Send Campaign",
      message: `Send "${item?.campaignName || id}" now?`,
      busy: false,
      onConfirm: async () => {
        setConfirm((p) => ({ ...p, busy: true }));
        try {
          await sendMarketingCampaign(id);
          closeConfirm();
          await load();
        } catch (e) {
          setErr(e?.message || "Send failed");
          setConfirm((p) => ({ ...p, busy: false }));
        }
      },
    });
  };

  const tableRows = useMemo(() => rows || [], [rows]);

  return (
    <div className="w-full">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-white font-bold text-lg">Marketing Campaigns</div>
          <div className="text-white/70 text-xs">Create, update, and send campaigns.</div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white flex items-center gap-2"
          >
            <RefreshCcw className="w-4 h-4" />
            Refresh
          </button>

          <button
            onClick={openCreate}
            className="px-3 py-2 rounded-xl bg-gradient-to-r from-pink-500 to-purple-600 text-white flex items-center gap-2 shadow-[0_0_18px_rgba(255,0,255,0.18)]"
          >
            <Plus className="w-4 h-4" />
            Add Campaign
          </button>
        </div>
      </div>

      {err && (
        <div className="mb-4 rounded-xl border border-red-400/30 bg-red-500/10 text-red-100 px-4 py-3 text-sm">
          {err}
        </div>
      )}

      <div className="backdrop-blur-2xl bg-white/10 border border-white/20 rounded-2xl p-4 shadow-[0_0_35px_rgba(255,0,255,0.14)]">
        {loading ? (
          <div className="text-white/80 text-sm">Loading...</div>
        ) : tableRows.length === 0 ? (
          <div className="text-white/70 text-sm">No campaigns yet.</div>
        ) : (
          <div className="overflow-auto">
            <table className="min-w-[900px] w-full text-sm text-white">
              <thead className="text-white/80">
                <tr className="border-b border-white/10">
                  <th className="text-left p-3">Name</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-left p-3">Channel</th>
                  <th className="text-left p-3">Scheduled</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((r) => {
                  const id = getId(r);
                  return (
                    <tr key={id} className="border-b border-white/10 hover:bg-white/5">
                      <td className="p-3 font-semibold">{r.campaignName}</td>
                      <td className="p-3">{r.campaignType}</td>
                      <td className="p-3">{r.channelType}</td>
                      <td className="p-3">{r.scheduledDate ? String(r.scheduledDate).replace("T", " ").slice(0, 16) : "-"}</td>
                      <td className="p-3">{r.status || "-"}</td>
                      <td className="p-3">
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => openEdit(r)}
                            className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white flex items-center gap-2"
                          >
                            <Pencil className="w-4 h-4" />
                            Edit
                          </button>

                          <button
                            onClick={() => askSend(r)}
                            className="px-3 py-2 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 text-white flex items-center gap-2"
                          >
                            <Send className="w-4 h-4" />
                            Send
                          </button>

                          <button
                            onClick={() => askDelete(r)}
                            className="px-3 py-2 rounded-xl bg-red-500/20 hover:bg-red-500/30 text-white flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <MarketingCampaignModal
        open={modalOpen}
        mode={editing ? "edit" : "create"}
        initial={editing}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSubmit={onSubmitModal}
      />

      <ConfirmModal
        open={confirm.open}
        title={confirm.title}
        message={confirm.message}
        busy={confirm.busy}
        onCancel={closeConfirm}
        onConfirm={confirm.onConfirm}
        confirmText="Yes"
      />
    </div>
  );
}
